/* One disclosure controller for every category; no catalog or cart mutations. */
(() => {
  'use strict';
  if (window.__lhUnifiedNavigation) return;
  window.__lhUnifiedNavigation = true;
  const fine = matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const nodes = new WeakMap();
  const panelOwners = new WeakMap();
  const states = new Set();
  const snapshots = new Map();
  let serial = 0, leaveTimer, positionFrame = 0;
  const direct = (el, selector) => Array.from(el.children).find(child => child.matches(selector));
  const floating = state => fine.matches && state.root.dataset.lhNav === 'desktop';
  const persistent = state => state.root.dataset.lhNav === 'sidebar';
  const storageKey = state => `lh-navigation-open-v1:${state.root.dataset.lhNav}`;
  function remember(state) {
    if (!persistent(state)) return;
    const snapshot = {path: location.pathname, open: state.records.filter(r => r.details.open).map(r => r.key)};
    snapshots.set(storageKey(state), snapshot);
    try { sessionStorage.setItem(storageKey(state), JSON.stringify(snapshot)); } catch (_) { /* Storage may be disabled. */ }
  }
  function restore(state) {
    if (!persistent(state)) return null;
    let saved = snapshots.get(storageKey(state));
    if (!saved) {
      try { saved = JSON.parse(sessionStorage.getItem(storageKey(state)) || 'null'); } catch (_) { /* Keep navigation usable without storage. */ }
    }
    if (!saved || !Array.isArray(saved.open) || typeof saved.path !== 'string') return null;
    const keys = new Set(saved.open.filter(key => typeof key === 'string'));
    state.records.forEach(record => {
      if (keys.has(record.key) && (!record.parent || record.parent.details.open)) open(record);
    });
    return saved;
  }
  function reposition() {
    if (positionFrame) return;
    positionFrame = requestAnimationFrame(() => {
      positionFrame = 0;
      states.forEach(state => {
        if (persistent(state)) state.records.forEach(record => { if (record.portal) position(record); });
      });
    });
  }
  const stateOf = target => {
    if (!(target instanceof Element)) return null;
    const container = target.closest('[data-lh-nav]');
    return Array.from(states).find(s => s.root === container || s.portals.has(container)) || null;
  };
  const animate = panel => {
    if (!reduced.matches && typeof panel.animate === 'function') {
      panel.getAnimations().forEach(a => a.cancel());
      panel.animate([{opacity: 0, transform: 'translateX(-8px)'}, {opacity: 1, transform: 'translateX(0)'}], {duration: 220, easing: 'ease-out'});
    }
  };
  function close(record) {
    record.state.records.filter(r => r.parent === record).forEach(close);
    if (record.portal) {
      record.placeholder.replaceWith(record.panel);
      record.state.portals.delete(record.portal);
      record.portal.remove(); record.portal = null;
    }
    record.details.removeAttribute('data-lh-floating');
    record.details.open = false;
    record.summary.setAttribute('aria-expanded', 'false');
  }
  function closeState(state) { state.records.filter(r => !r.parent).forEach(close); }
  function position(record) {
    const box = record.summary.getBoundingClientRect();
    const portal = record.portal;
    const width = Math.min(280, innerWidth - 16);
    portal.style.width = `${width}px`;
    const height = Math.min(portal.scrollHeight + 2, innerHeight - 16);
    let left = box.right;
    if (left + width > innerWidth - 8) left = box.left - width;
    left = Math.max(8, Math.min(left, innerWidth - width - 8));
    const top = Math.max(8, Math.min(box.top, innerHeight - height - 8));
    portal.style.left = `${left}px`; portal.style.top = `${top}px`;
  }
  function open(record, focusChild = false) {
    clearTimeout(leaveTimer);
    if (!persistent(record.state) || floating(record.state)) {
      record.state.records.filter(r => r !== record && r.parent === record.parent).forEach(close);
    }
    if (!record.details.open) {
      record.details.open = true;
      record.summary.setAttribute('aria-expanded', 'true');
      if (floating(record.state)) {
        record.placeholder = document.createComment('LionHeart submenu position');
        record.panel.before(record.placeholder);
        const portal = document.createElement('div');
        portal.className = 'lh-unified-nav lh-nav-floating';
        portal.dataset.lhNav = 'floating';
        const style = getComputedStyle(record.state.root);
        ['--lh-nav-bg', '--lh-nav-text', '--lh-nav-accent', '--navigationFontFamily', '--navigationFontStyle', '--navigationFontWeight'].forEach(key => {
          const value = style.getPropertyValue(key).trim();
          if (value) portal.style.setProperty(key, value);
        });
        portal.append(record.panel); document.body.append(portal);
        record.portal = portal; record.state.portals.add(portal);
        record.details.setAttribute('data-lh-floating', '');
        position(record); animate(portal);
      } else animate(record.panel);
    }
    if (focusChild) record.panel.querySelector('a, summary')?.focus();
  }
  function markCurrent(state, saved) {
    const current = new URL(location.href);
    const normalize = path => decodeURI(path).replace(/\/$/, '') || '/';
    const ignored = new Set(['preview_theme_id', 'filter.v.availability', 'sort_by', 'page']);
    const matches = [];
    const links = new Set();
    [state.root, ...state.portals].forEach(container => container.querySelectorAll('a[href]').forEach(a => links.add(a)));
    links.forEach(a => {
      try {
        const target = new URL(a.getAttribute('href'), location.href);
        if (target.origin !== current.origin || normalize(target.pathname) !== normalize(current.pathname) || target.hash) return;
        const params = Array.from(target.searchParams).filter(([key]) => !ignored.has(key));
        if (!params.every(([key, value]) => current.searchParams.getAll(key).includes(value))) return;
        matches.push({a, score: params.length});
      } catch (_) { /* An invalid legacy URL must never interrupt other menus. */ }
    });
    const best = Math.max(-1, ...matches.map(m => m.score));
    matches.filter(m => m.score === best).forEach(({a}) => {
      a.setAttribute('aria-current', 'page');
      const trail = [];
      let record = panelOwners.get(a.closest('ul'));
      while (record && record.state === state) {
        record.details.setAttribute('data-lh-active', '');
        trail.unshift(record); record = record.parent;
      }
      const reveal = persistent(state) && (!saved || saved.path !== location.pathname);
      if (reveal) trail.forEach(record => open(record));
    });
  }
  function initialize() {
    states.forEach(state => { if (!state.root.isConnected) { closeState(state); states.delete(state); } });
    document.querySelectorAll('[data-lh-nav]:not([data-lh-nav="floating"])').forEach(root => {
      if (root.dataset.lhReady) return;
      root.dataset.lhReady = 'true';
      const state = {root, records: [], portals: new Set()}; states.add(state);
      root.querySelectorAll('details').forEach(details => {
        const summary = direct(details, 'summary'), panel = direct(details, 'ul');
        if (!summary || !panel) return;
        const parentDetails = details.parentElement.closest('details');
        const parent = state.records.find(r => r.details === parentDetails) || null;
        const label = encodeURIComponent(summary.textContent.trim().replace(/\s+/g, ' '));
        const key = `${parent ? parent.key + '/' : ''}${label}`;
        const record = {state, details, summary, panel, parent, key, portal: null};
        details.open = false;
        panel.id = `lh-navigation-panel-${++serial}`;
        summary.setAttribute('aria-controls', panel.id);
        summary.setAttribute('aria-expanded', 'false');
        nodes.set(summary, record); panelOwners.set(panel, record); state.records.push(record);
      });
      const headerDisclosure = root.closest('details.yv-dropdown-detail');
      if (headerDisclosure && !persistent(state)) headerDisclosure.addEventListener('toggle', () => { if (!headerDisclosure.open) closeState(state); });
      const saved = restore(state);
      markCurrent(state, saved);
      remember(state);
    });
  }
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const summary = target?.closest('summary');
    const record = summary && nodes.get(summary);
    if (record) {
      event.preventDefault(); event.stopPropagation();
      record.details.open ? close(record) : open(record); remember(record.state); return;
    }
    const state = stateOf(target);
    if (!state) states.forEach(s => { if (!persistent(s)) closeState(s); });
    else remember(state);
    // A link in a floating submenu navigates normally; no preventDefault.
  }, true);
  document.addEventListener('keydown', event => {
    const target = event.target instanceof Element ? event.target : null;
    const state = stateOf(target); if (!state) return;
    const record = nodes.get(target?.closest('summary'));
    if (event.key === 'ArrowRight' && record) {
      event.preventDefault(); event.stopPropagation(); open(record, true);
    } else if (event.key === 'Escape' || event.key === 'ArrowLeft') {
      const owner = record?.details.open ? record : panelOwners.get(target?.closest('ul'));
      if (owner) { event.preventDefault(); event.stopPropagation(); close(owner); owner.summary.focus(); }
      else if (event.key === 'Escape') closeState(state);
    }
    remember(state);
  }, true);
  document.addEventListener('pointerover', event => {
    const target = event.target instanceof Element ? event.target : null;
    const state = stateOf(target);
    if (state) clearTimeout(leaveTimer);
    if (!state || !floating(state) || event.pointerType === 'touch') return;
    const summary = target.closest('summary');
    const record = summary && nodes.get(summary);
    if (record && !summary.contains(event.relatedTarget)) open(record);
    if (target.closest('a') && !persistent(state)) {
      const parent = panelOwners.get(target.closest('ul')) || null;
      state.records.filter(r => r.parent === parent).forEach(close);
    }
    remember(state);
  });
  document.addEventListener('pointerout', event => {
    const state = stateOf(event.target);
    if (state && !persistent(state) && floating(state) && stateOf(event.relatedTarget) !== state) {
      clearTimeout(leaveTimer); leaveTimer = setTimeout(() => closeState(state), 180);
    }
  });
  document.addEventListener('scroll', event => {
    const scrolled = stateOf(event.target);
    const insidePortal = scrolled?.portals.has(event.target.closest?.('[data-lh-nav]'));
    if (!insidePortal) states.forEach(state => { if (floating(state) && !persistent(state)) closeState(state); });
    reposition();
  }, true);
  window.addEventListener('resize', () => {
    states.forEach(state => {
      if (!persistent(state)) { closeState(state); return; }
      const expanded = state.records.filter(r => r.details.open);
      if (expanded.some(r => Boolean(r.portal) !== floating(state))) {
        closeState(state);
        expanded.forEach(record => { if (!record.parent || record.parent.details.open) open(record); });
      }
      remember(state);
    });
    reposition();
  });
  window.addEventListener('pageshow', initialize);
  const watch = () => {
    initialize();
    let pending = false;
    new MutationObserver(changes => {
      if (pending || !changes.some(change => Array.from(change.addedNodes).some(node => node.nodeType === 1 && (node.matches?.('[data-lh-nav]:not([data-lh-nav="floating"])') || node.querySelector?.('[data-lh-nav]:not([data-lh-nav="floating"])'))))) return;
      pending = true; queueMicrotask(() => { pending = false; initialize(); });
    }).observe(document.body, {childList: true, subtree: true});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch, {once: true}); else watch();
})();
